import { 
  signInWithPopup, 
  GoogleAuthProvider, 
  signOut,
  onAuthStateChanged,
  User
} from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from './firebase';
import { AuthUser, UserPermission, PermissionLevel } from '../types';

// Google 인증 제공자
const googleProvider = new GoogleAuthProvider();
googleProvider.setCustomParameters({
  prompt: 'select_account'
});

// Google 로그인
export const signInWithGoogle = async (): Promise<AuthUser> => {
  try {
    const result = await signInWithPopup(auth, googleProvider);
    const user = result.user;
    
    return {
      uid: user.uid,
      email: user.email,
      displayName: user.displayName,
      photoURL: user.photoURL
    };
  } catch (error: any) {
    console.error('Google 로그인 실패:', error);
    throw new Error(`로그인 실패: ${error.message}`);
  }
};

// 로그아웃
export const logout = async (): Promise<void> => {
  try {
    await signOut(auth);
  } catch (error: any) {
    console.error('로그아웃 실패:', error);
    throw new Error(`로그아웃 실패: ${error.message}`);
  }
};

// 사용자 권한 확인
export const getUserPermissions = async (email: string): Promise<UserPermission | null> => {
  try {
    // Firestore에서 권한 확인 (우선)
    const permissionDoc = await getDoc(doc(db, 'permissions', email));
    
    if (permissionDoc.exists()) {
      return permissionDoc.data() as UserPermission;
    }
    
    // Firestore에 권한이 없으면 시스템 관리자 체크
    if (email === process.env.REACT_APP_ADMIN_EMAIL) {
      return {
        email,
        level: 'admin' as const,
        canEdit: true,
        grantedBy: 'system',
        grantedAt: new Date() as any
      };
    }
    
    return null;
  } catch (error: any) {
    console.error('권한 확인 실패:', error);
    return null;
  }
};

// 인증 상태 변경 리스너
export const onAuthStateChange = (callback: (user: User | null) => void) => {
  return onAuthStateChanged(auth, callback);
};

// 현재 사용자 정보 가져오기
export const getCurrentUser = (): User | null => {
  return auth.currentUser;
};

// 현재 사용자의 토큰 가져오기
export const getCurrentUserToken = async (): Promise<string | null> => {
  const user = getCurrentUser();
  if (!user) return null;
  
  try {
    return await user.getIdToken();
  } catch (error) {
    console.error('토큰 가져오기 실패:', error);
    return null;
  }
};

// 사용자 편집 권한 확인
export const canUserEdit = async (userEmail: string): Promise<boolean> => {
  try {
    // 관리자인지 확인
    if (userEmail === process.env.REACT_APP_ADMIN_EMAIL) {
      return true;
    }

    // 권한 문서에서 확인
    const permissions = await getUserPermissions(userEmail);
    return permissions?.canEdit ?? false;
  } catch (error) {
    console.error('편집 권한 확인 실패:', error);
    return false;
  }
};

// 현재 사용자가 관리자인지 확인
export const isCurrentUserAdmin = (): boolean => {
  const user = getCurrentUser();
  return user?.email === process.env.REACT_APP_ADMIN_EMAIL;
};
